// Metronici18n.tsx (provider)
import React, {FC, useCallback, useContext, useEffect, useState} from 'react'
import {useAuth} from '../../app/modules/auth'
import CommonService from '../../app/modules/common/core/_requests'
import moment from 'moment'
import {LANGUAGE_KEY} from '../config/LanguageConfig'

type LanguageProviderModel = {
  language: string
  setLanguage: React.Dispatch<React.SetStateAction<string>>
  handleUpdateLanguage: (userId: string | number, language: string) => Promise<void>
}

const initLanguageContextPropsState: LanguageProviderModel = {
  language: 'en',
  setLanguage: () => {},
  handleUpdateLanguage: async () => {},
}

const getStoredPreferredLanguage = (): string | null => {
  try {
    return localStorage.getItem('preferred_language')
  } catch {
    return null
  }
}

const normalizeMomentLocale = (code: string) =>
  code?.toLowerCase() === 'zh' || code?.toLowerCase() === LANGUAGE_KEY.ZH
    ? 'zh-cn'
    : code?.toLowerCase()

const I18nContext = React.createContext<LanguageProviderModel>(initLanguageContextPropsState)

const MetronicI18nProvider: FC = ({children}) => {
  const {currentUser} = useAuth()

  const [language, setLanguage] = useState<string>(() => {
    const stored = getStoredPreferredLanguage()
    return stored || 'en'
  })

  const applyLanguage = (lang: string) => {
    setLanguage(lang)
    moment.locale(normalizeMomentLocale(lang))
  }

  useEffect(() => {
    // If user is logged in, their profile language takes priority
    if (currentUser?.user_profiles?.general?.language) {
      const userLang = currentUser.user_profiles.general.language
      // Clear public toggle preference when logged in
      localStorage.removeItem('preferred_language')
      if (userLang !== language) applyLanguage(userLang)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.user_profiles?.general?.language])

  useEffect(() => {
    const sync = () => {
      // Only sync public toggle if not logged in
      if (!currentUser) {
        const storedLang = getStoredPreferredLanguage()
        if (storedLang && storedLang !== language) {
          applyLanguage(storedLang)
        }
      }
    }

    sync()

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'preferred_language' && !currentUser) sync()
    }

    const onPreferredLanguageChanged = (e: Event) => {
      if (!currentUser) {
        const lang = (e as CustomEvent)?.detail?.language as string | undefined
        const storedLang = lang ?? getStoredPreferredLanguage()
        if (storedLang) applyLanguage(storedLang)
      }
    }

    window.addEventListener('storage', onStorage)
    window.addEventListener('preferredLanguageChanged', onPreferredLanguageChanged as EventListener)

    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(
        'preferredLanguageChanged',
        onPreferredLanguageChanged as EventListener
      )
    }
  }, [currentUser, language])

  const handleUpdateLanguage = useCallback(async (userId: string | number, lang: string) => {
    await CommonService.updateLanguage(userId, {language: lang})
    window.location.reload()
  }, [])

  return (
    <I18nContext.Provider value={{language, setLanguage, handleUpdateLanguage}}>
      {children}
    </I18nContext.Provider>
  )
}

const useLanguage = () => useContext(I18nContext)
export {MetronicI18nProvider, useLanguage}
